import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { CARD_SIDE, ORIENTATION } from '../../../../cards/constants';
import usePrintProductConfiguration from '../../PrintProductConfigurator/usePrintProductConfiguration';
import DesignEditorLoadingIndicator from '../../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import AccountCard from '../../../../cards/components/AccountCard';
import NativeActivityIndicator from '../../../../../components/global/NativeActivityIndicator';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../util/normalize-errors';
import PrintBackgroundSelectorModal from '../../PrintBackgroundSelector';
import { getAccountCardElementsConfiguration } from '../../../../cards/components/AccountCard/helpers';
import { isElementCardBackground } from '../../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';

const propTypes = {
  printProductId: PropTypes.string,
  printProductSizeId: PropTypes.string,
  orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  defaultAccountCard: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onAccountCardUpdated: PropTypes.func,
  printBackgrounds: PropTypes.arrayOf({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  printBackgroundModalOpen: PropTypes.bool,
  setPrintBackgroundModalOpen: PropTypes.func,
  className: PropTypes.string,
};

const PrintProductGroupConfigurationItem = ({
  printProductId,
  printProductSizeId,
  printProductSizeServiceIds,
  orientation,
  defaultAccountCard,
  albumMembershipGroup,
  identity,
  photo,
  onAccountCardUpdated,
  printBackgrounds,
  printBackgroundModalOpen,
  setPrintBackgroundModalOpen,
  className,
}) => {
  const openAlert = useOpenAlert();
  const [
    accountCardElementReferences,
    setAccountCardElementReferences,
  ] = useState({});

  const {
    printProduct,
    printProductConfiguration: { selectedPrintProductSize },
    accountCardConfiguration: {
      loadingAccountCard,
      accountCard,
      onElementConfigurationChange,
      cardElementLoading,
    },
  } = usePrintProductConfiguration({
    printProductId,
    defaultPrintProductSizeId: printProductSizeId,
    defaultPrintProductSizeServiceIds: printProductSizeServiceIds,
    defaultPhoto: photo,
    defaultAccountCardId: defaultAccountCard?.id,
    defaultOrientation: orientation,
    onError: error => {
      openAlert({
        type: 'error',
        message:
          'It looks like an error ocurred while we were trying to configure your prints. Please start the configuration over.',
        info: getErrorString(error),
      });
    },
  });

  const state = useMemo(
    () => getAccountCardElementsConfiguration(accountCard, cardElementLoading),
    [accountCard, cardElementLoading],
  );

  const backgroundElement = useMemo(
    () =>
      state.find(e => e.side === CARD_SIDE.FRONT && isElementCardBackground(e)),
    [state],
  );

  useEffect(() => {
    if (selectedPrintProductSize?.id) {
      onAccountCardUpdated({
        printProduct,
        printProductSize: selectedPrintProductSize,
        accountCard,
        accountCardElementReferences,
      });
    }
  }, [
    printProduct?.id,
    selectedPrintProductSize?.id,
    accountCard?.id,
    // Listen to changes within account_card_sides
    // users should have valid configurations, hence why we
    // should update the account card sides whenever possible.
    accountCard?.account_card_sides,
    backgroundElement,
    accountCardElementReferences,
  ]);

  useEffect(() => {
    const setBackground = async () => {
      const photoPrintBackground = photo?.print_background;

      if (
        printBackgrounds?.length > 0 &&
        !backgroundElement?.cardElementConfiguration?.asset_image?.id
      ) {
        const isPrintBackdropInPackage = !!printBackgrounds?.find(
          ({ id }) => id === photoPrintBackground?.id,
        );
        await onElementConfigurationChange(backgroundElement, {
          photo_id: null,
          // We need to keep the element non-editable for it to work properly.
          is_editable: false,
          existing_asset_image_id: isPrintBackdropInPackage
            ? photoPrintBackground?.asset_image?.id ??
              printBackgrounds?.[0]?.asset_image?.id
            : printBackgrounds?.[0]?.asset_image?.id,
        });
      }
    };

    setBackground();
  }, [backgroundElement, photo]);

  const onAddAccountCardElementReference = ({ cardElementId, ref }) => {
    setAccountCardElementReferences({
      ...accountCardElementReferences,
      [cardElementId]: ref,
    });
  };

  const classes = classNames({
    'is-loading': loadingAccountCard,
    [className]: !!className,
  });

  if (!printProduct) {
    return <DesignEditorLoadingIndicator />;
  }

  if (loadingAccountCard && !accountCard) {
    return (
      <div className={classes}>
        <NativeActivityIndicator />
      </div>
    );
  }

  return (
    <Fragment>
      {accountCard && (
        <Fragment>
          <AccountCard
            className="print-product-configurator-account-card"
            onElementConfigurationChange={onElementConfigurationChange}
            onAddAccountCardElementReference={onAddAccountCardElementReference}
            accountCard={accountCard}
            cardElementLoading={cardElementLoading}
            photosContext={{
              identity_id: identity?.id,
              album_id:
                albumMembershipGroup &&
                (albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              album_folder_id:
                albumMembershipGroup &&
                (!albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              root_album_folder_id:
                albumMembershipGroup && albumMembershipGroup.is_album
                  ? albumMembershipGroup.album.root_album_folder?.id
                  : undefined,
              is_album_membership_group: !!albumMembershipGroup,
              uploads_disabled: true,
              grid_select_disabled: true,
            }}
            allowPhotoChange
            defaultPhoto={photo}
          />
          <PrintBackgroundSelectorModal
            forceOpen={printBackgroundModalOpen}
            onClose={() => setPrintBackgroundModalOpen(false)}
            printBackgrounds={printBackgrounds}
            onPrintBackgroundSelect={async ({ printBackground }) => {
              if (printBackground.asset_image) {
                await onElementConfigurationChange(backgroundElement, {
                  photo_id: null,
                  // We need to keep the element non-editable for it to work properly.
                  is_editable: false,
                  existing_asset_image_id: printBackground?.asset_image?.id,
                });
              }
              setPrintBackgroundModalOpen(false);
            }}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

PrintProductGroupConfigurationItem.propTypes = propTypes;

export default PrintProductGroupConfigurationItem;
